import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import Header from '../components/navbar/Header';
import Navbar from '../components/navbar/Navbar';
import Footer from '../components/footer/Footer';
import ScrollToTop from '../utils/scroll/ScrolltoTop';
import Loader from '../components/loader/Loader';
import LazyLoader from '../components/lazyloader/LazyLoader';
import { useUser } from '../context/useUser';
import { jwtDecode } from 'jwt-decode';
import { Toaster } from 'sonner';
import { SECRET_KEY } from '../constant';
import { useCart } from '../context/useCart';
import Advertise from './client/advertise/Advertisement';
import WhatsAppFloatingButton from '../components/whatsapp/WhatsAppFloatingButton';

// Lazy loading components
const LandingPage = lazy(() => import('./client/landing/LandingPage'));
const BeefShop = lazy(() => import('./client/shop/BeefShop'));
const MuttonShop = lazy(() => import('./client/shop/MuttonShop'));
const ChickenShop = lazy(() => import('./client/shop/ChickenShop'));
const ProductDescription = lazy(() => import('./client/product/ProductDescription'));
const CartShopping = lazy(() => import('./client/cart/CartShopping'));
const WishList = lazy(() => import('./client/wishlist/WishList'));
const About = lazy(() => import('./client/about/About'));
const Contact = lazy(() => import('./client/contact/Contact'));
const SignIn = lazy(() => import('./auth/SignIn'));
const Signup = lazy(() => import('./auth/SignUp'));
const ForgetPassword = lazy(() => import('./auth/forgetPassword'));
const OtpScreen = lazy(() => import('./auth/OtpScreen'));
const ConfirmPassword = lazy(() => import('./auth/ConfirmPassword'));
const AdminPortal = lazy(() => import('./auth/AdminLogin'));
const Dashboard = lazy(() => import('./admin/dashboard/Dashboard'));
const CountryVersion = lazy(() => import('../components/version/CountryVersion'));
const Qurbani = lazy(() => import('./client/qurbani/Qurbani'));
const DOnateQurbani = lazy(() => import('./client/qurbani/DonateQurbani'));
const ClientPortal = lazy(() => import('./client/portal/ClientPortal'));
const QurbaniDescription = lazy(() => import('./client/qurbani/QurbaniDescription'));
const PaymentSuccess = lazy(() => import('./../components/transaction-screens/Success'));
const PaymentFailure = lazy(() => import('./../components/transaction-screens/Failure'));
const PrivacyPolicy = lazy(() => import('./../components/Terms-conditions/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./../components/Terms-conditions/TermsAndConditions'));
const FAQ = lazy(() => import('./../components/Terms-conditions/FAQ'));

function Main() {
    const { setCurrentUser, isLoggedIn, setIsLoggedIn, currentUser, setUserPermissions, setCountry, setIsCustomerLoggedIn, setCustomer, isCustomerLoggedIn, customer, country } = useUser();
    const location = useLocation();
    const navigate = useNavigate();
    const { setCart } = useCart()

    const isValid = (value) => value && value !== 'null' && value !== 'undefined';

    useEffect(() => {
        const checkIfLoggedIn = () => {
            const token = localStorage.getItem("token");
            const user = localStorage.getItem("currentSessionUser");
            const permissions = localStorage.getItem('authorities');
            const encryptedRegion = localStorage.getItem("region");
            const regionExpiry = localStorage.getItem("regionExpiry");

            if (isValid(encryptedRegion)) {
                let reversed = encryptedRegion.split("").reverse().join("");
                let decoded = atob(reversed);
                const region = decoded.replace(SECRET_KEY, "");
                setCountry(region);
            }

            if (regionExpiry && Date.now() > parseInt(regionExpiry, 10) || !encryptedRegion) {
                localStorage.removeItem("region");
                localStorage.removeItem("regionExpiry");
                navigate("/country-version");
                return;
            }

            if (isValid(user)) {
                setCurrentUser(JSON.parse(user));
            }

            if (isValid(token)) {
                try {
                    const decodedToken = jwtDecode(token);
                    const currentTime = Date.now() / 1000;
                    if (decodedToken.exp < currentTime) {
                        localStorage.removeItem("token");
                        localStorage.removeItem("currentSessionUser");
                        localStorage.removeItem('authorities');
                        setIsLoggedIn(false);
                    } else {
                        if (isValid(permissions)) {
                            setUserPermissions(permissions);
                            setIsLoggedIn(true);
                        } else {
                            setUserPermissions(null);
                            setIsLoggedIn(false);
                        }
                    }
                } catch (error) {
                    console.error("Invalid token:", error);
                    setIsLoggedIn(false);
                }
            } else {
                setIsLoggedIn(false);
                localStorage.removeItem("token");
                localStorage.removeItem("currentSessionUser");
                localStorage.removeItem('authorities');
            }
        };

        checkIfLoggedIn();
    }, []);


    useEffect(() => {
        const checkIfLoggedIn = () => {
            const bearer = localStorage.getItem("bearer");
            const customerData = localStorage.getItem("customer_");

            if (isValid(customerData)) {
                setCustomer(JSON.parse(customerData));
            }

            if (isValid(bearer)) {
                try {
                    const decodedToken = jwtDecode(bearer);
                    const currentTime = Date.now() / 1000;
                    if (decodedToken.exp < currentTime) {
                        localStorage.removeItem("bearer");
                        localStorage.removeItem("customer_");
                        setIsCustomerLoggedIn(false);
                    } else {
                        setIsCustomerLoggedIn(true);
                    }
                } catch (error) {
                    console.error("Invalid token:", error);
                    setIsCustomerLoggedIn(false);
                }
            } else {
                setIsCustomerLoggedIn(false);
                localStorage.removeItem("bearer");
                localStorage.removeItem("customer_");
            }
        };

        checkIfLoggedIn();
    }, []);


    useEffect(() => {
        if (location.pathname.startsWith('/country-version')) {
            localStorage.removeItem("cart")
            setCart([])
        }
    }, [location])

    const isDashboardRoute = (location.pathname.startsWith('/admin-portal') || location.pathname.startsWith('/dashboard'));
    const isAdminPortal = location.pathname.startsWith('/alifadmin');
    const isCustomerPortal = location.pathname.startsWith('/dashboard');
    const isSelectingCountry = location.pathname.startsWith('/country-version');
    const isAuthRoute = (location.pathname.startsWith('/auth') || location.pathname.startsWith('/signup') || location.pathname.startsWith('/forget-password') || location.pathname.startsWith('/verification') || location.pathname.startsWith('/confirm-password'));
    const isPaymentRoute = (location.pathname.startsWith('/payment-successful') || location.pathname.startsWith('/payment-failed'));
    return (
        <>
            <Toaster richColors closeButton position='top-right' duration={2000} />
            <ScrollToTop />
            <Loader />
            {(!isAuthRoute && !isDashboardRoute && !isCustomerPortal && !isSelectingCountry && !isAdminPortal && country === "PK" && !isPaymentRoute) && <WhatsAppFloatingButton />}
            {(!isAuthRoute && !isDashboardRoute && !isCustomerPortal && !isSelectingCountry && !isAdminPortal && !isPaymentRoute) && <Advertise />}
            {(!isDashboardRoute && !isAdminPortal && !isSelectingCountry && !isPaymentRoute) && (
                <>
                    {(!isAuthRoute) && <Header />}
                    <Navbar />
                </>
            )}
            <Suspense fallback={<LazyLoader />}>
                <Routes>
                    <Route path="/" element={isLoggedIn ? <Navigate to={`/admin-portal/${currentUser?.id}`} /> : <LandingPage />} />
                    <Route path="/auth" element={isCustomerLoggedIn ? <Navigate to={`/dashboard/${customer?.id}`} /> : <SignIn />} />
                    <Route path="/forget-password" element={<ForgetPassword />} />
                    <Route path="/verification" element={<OtpScreen />} />
                    <Route path="/confirm-password" element={<ConfirmPassword />} />
                    <Route path="/signup" element={isCustomerLoggedIn ? <Navigate to={`/dashboard/${customer?.id}`} /> : <Signup />} />
                    <Route path="/shop/beef" element={isLoggedIn ? <Navigate to={`/admin-portal/${currentUser?.id}`} /> : <BeefShop />} />
                    <Route path="/shop/mutton" element={isLoggedIn ? <Navigate to={`/admin-portal/${currentUser?.id}`} /> : <MuttonShop />} />
                    <Route path="/shop/chicken" element={isLoggedIn ? <Navigate to={`/admin-portal/${currentUser?.id}`} /> : <ChickenShop />} />
                    <Route path="/book-your-qurbani" element={isLoggedIn ? <Navigate to={`/admin-portal/${currentUser?.id}`} /> : <Qurbani />} />
                    <Route path="/donate-qurbani" element={isLoggedIn ? <Navigate to={`/admin-portal/${currentUser?.id}`} /> : <DOnateQurbani />} />
                    <Route path="/product/description/:productId" element={isLoggedIn ? <Navigate to={`/admin-portal/${currentUser?.id}`} /> : <ProductDescription />} />
                    <Route path="/qurbani/description/:qurbaniId" element={isLoggedIn ? <Navigate to={`/admin-portal/${currentUser?.id}`} /> : <QurbaniDescription />} />
                    <Route path="/cart" element={<CartShopping />} />
                    <Route path="/AlifAdmin" element={isLoggedIn ? <Navigate to={`/admin-portal/${currentUser?.id}`} /> : <AdminPortal />} />
                    <Route path="/wishlist" element={<WishList />} />
                    <Route path="/about-us" element={<About />} />
                    <Route path="/contact-us" element={<Contact />} />
                    <Route path="/admin-portal/:userId/*" element={<Dashboard />} />
                    <Route path="/dashboard/:user/*" element={<ClientPortal />} />
                    <Route path="/country-version" element={<CountryVersion />} />
                    <Route path="/payment-successful" element={<PaymentSuccess />} />
                    <Route path="/payment-failed" element={<PaymentFailure />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/terms-conditions" element={<TermsAndConditions />} />
                    <Route path="/f-a-q" element={<FAQ />} />
                </Routes>
            </Suspense>
            {(!isDashboardRoute && !isCustomerPortal && !isAuthRoute && !isPaymentRoute) && <Footer />}
        </>
    );
}

export default Main;
